import { useGameCreator } from "@/contexts/GameCreatorContext";
import { motion } from "framer-motion";
import {
  Trophy, Users, Clock, DollarSign, TrendingUp, Shield, Copy, Check,
  Image, FileText, Video, Link2, Hash, Vote, Share2, Megaphone, Globe, Zap
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";

function ViralGauge({ score }: { score: number }) {
  const circumference = 2 * Math.PI * 42;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? "oklch(0.72 0.15 195)" : score >= 50 ? "oklch(0.80 0.16 85)" : "oklch(0.60 0.01 260)";

  return (
    <div className="relative w-28 h-28 mx-auto">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r="42" fill="none" stroke="oklch(0.22 0.02 260)" strokeWidth="6" />
        <motion.circle
          cx="50"
          cy="50"
          r="42"
          fill="none"
          stroke={color}
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1, ease: "easeOut" }}
          style={{ filter: `drop-shadow(0 0 6px ${color})` }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.span
          className="text-2xl font-bold"
          style={{ color }}
          key={score}
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 200 }}
        >
          {score}
        </motion.span>
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Viral</span>
      </div>
    </div>
  );
}

function ComplianceBar({ score }: { score: number }) {
  const color = score >= 80 ? "bg-green-500" : score >= 50 ? "bg-gold" : "bg-muted";
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground flex items-center gap-1">
          <Shield className="w-3 h-3" /> Compliance
        </span>
        <span className="font-mono text-foreground">{score}/100</span>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <motion.div
          className={`h-full rounded-full ${color}`}
          initial={{ width: 0 }}
          animate={{ width: `${score}%` }}
          transition={{ duration: 0.8 }}
        />
      </div>
    </div>
  );
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleCopy}
      className="text-xs h-7 gap-1 border-border/50 hover:border-neon/50 hover:text-neon"
    >
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {copied ? "Copied!" : label}
    </Button>
  );
}

export default function GamePreview() {
  const { gameConfig, phase } = useGameCreator();
  // Prize pool = total entry fees minus 15% platform fee
  const prizePool = gameConfig.entryFee * gameConfig.maxPlayers * 0.85;
  const hasTitle = gameConfig.title.length > 0;

  return (
    <div className="h-full overflow-y-auto space-y-4 p-4">
      {/* Viral Score Gauge */}
      <div className="glass-panel rounded-xl p-4 text-center">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          Viral Potential
        </h3>
        <ViralGauge score={gameConfig.viralScore} />
      </div>

      {/* Game Preview Card */}
      <div className="glass-panel rounded-xl p-4 space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Game Preview
        </h3>

        <div className="space-y-2">
          <h4 className="text-lg font-bold text-gradient-neon">
            {hasTitle ? gameConfig.title : "Your Game Title"}
          </h4>
          {gameConfig.description && (
            <p className="text-xs text-muted-foreground leading-relaxed">
              {gameConfig.description}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="bg-secondary/50 rounded-lg p-2.5 space-y-0.5">
            <div className="flex items-center gap-1 text-muted-foreground">
              <DollarSign className="w-3 h-3" />
              <span className="text-[10px] uppercase">Entry</span>
            </div>
            <p className="text-sm font-bold font-mono">${gameConfig.entryFee.toFixed(2)}</p>
          </div>
          <div className="bg-secondary/50 rounded-lg p-2.5 space-y-0.5">
            <div className="flex items-center gap-1 text-muted-foreground">
              <Users className="w-3 h-3" />
              <span className="text-[10px] uppercase">Players</span>
            </div>
            <p className="text-sm font-bold font-mono">{gameConfig.maxPlayers}</p>
          </div>
          <div className="bg-secondary/50 rounded-lg p-2.5 space-y-0.5">
            <div className="flex items-center gap-1 text-muted-foreground">
              <Trophy className="w-3 h-3" />
              <span className="text-[10px] uppercase">Prize Pool</span>
            </div>
            <p className="text-sm font-bold font-mono text-gold">${prizePool.toFixed(0)}</p>
          </div>
          <div className="bg-secondary/50 rounded-lg p-2.5 space-y-0.5">
            <div className="flex items-center gap-1 text-muted-foreground">
              <Clock className="w-3 h-3" />
              <span className="text-[10px] uppercase">Duration</span>
            </div>
            <p className="text-sm font-bold font-mono">{gameConfig.duration}</p>
          </div>
        </div>

        {/* Revenue Split */}
        {(gameConfig.hostCut > 0 || gameConfig.promoterCut > 0) && (
          <div className="space-y-1.5">
            <span className="text-[10px] uppercase text-muted-foreground">Revenue Split</span>
            <div className="flex h-3 rounded-full overflow-hidden bg-muted">
              <div
                className="bg-neon/80 transition-all"
                style={{ width: `${100 - gameConfig.hostCut - gameConfig.promoterCut - 15}%` }}
                title="Winners"
              />
              <div
                className="bg-gold/80 transition-all"
                style={{ width: `${gameConfig.promoterCut}%` }}
                title="Promoter"
              />
              <div
                className="bg-foreground/20 transition-all"
                style={{ width: `${gameConfig.hostCut}%` }}
                title="Host"
              />
              <div
                className="bg-muted-foreground/30 transition-all"
                style={{ width: "10%" }}
                title="Platform"
              />
            </div>
            <div className="flex justify-between text-[9px] text-muted-foreground">
              <span className="text-neon">Winners {100 - gameConfig.hostCut - gameConfig.promoterCut - 15}%</span>
              <span className="text-gold">Promoter {gameConfig.promoterCut}%</span>
              <span>Host {gameConfig.hostCut}%</span>
            </div>
          </div>
        )}

        <ComplianceBar score={gameConfig.complianceScore} />
      </div>

      {/* Game Type & Audience */}
      {(gameConfig.gameType || gameConfig.targetAudience) && (
        <div className="glass-panel rounded-xl p-4 space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Configuration
          </h3>
          {gameConfig.gameType && (
            <div className="flex items-center gap-2 text-sm">
              <TrendingUp className="w-3.5 h-3.5 text-neon" />
              <span className="text-muted-foreground">Type:</span>
              <span className="font-medium">{gameConfig.gameType}</span>
            </div>
          )}
          {gameConfig.targetAudience && (
            <div className="flex items-center gap-2 text-sm">
              <Users className="w-3.5 h-3.5 text-gold" />
              <span className="text-muted-foreground">Audience:</span>
              <span className="font-medium">{gameConfig.targetAudience}</span>
            </div>
          )}
          {gameConfig.winCondition && (
            <div className="flex items-start gap-2 text-sm">
              <Trophy className="w-3.5 h-3.5 text-neon mt-0.5" />
              <span className="text-muted-foreground">Win:</span>
              <span className="font-medium">{gameConfig.winCondition}</span>
            </div>
          )}
        </div>
      )}

      {/* Submission & Voting Mechanics */}
      {(gameConfig.submissionType || gameConfig.votingMethod) && (
        <div className="glass-panel rounded-xl p-4 space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Viral Loop Mechanics
          </h3>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              {gameConfig.submissionType === "image" ? <Image className="w-3.5 h-3.5 text-neon" /> :
               gameConfig.submissionType === "text" ? <FileText className="w-3.5 h-3.5 text-neon" /> :
               gameConfig.submissionType === "video" ? <Video className="w-3.5 h-3.5 text-neon" /> :
               gameConfig.submissionType === "link" ? <Link2 className="w-3.5 h-3.5 text-neon" /> :
               <Hash className="w-3.5 h-3.5 text-neon" />}
              <span className="text-muted-foreground">Submit:</span>
              <Badge variant="outline" className="text-[10px] border-neon/30 text-neon">
                {gameConfig.submissionType.replace("_", " ").toUpperCase()}
              </Badge>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Vote className="w-3.5 h-3.5 text-gold" />
              <span className="text-muted-foreground">Voting:</span>
              <Badge variant="outline" className="text-[10px] border-gold/30 text-gold">
                {gameConfig.votingMethod.replace("_", " ").toUpperCase()}
              </Badge>
            </div>
            {gameConfig.influencerChannelUrl && (
              <div className="flex items-center gap-2 text-sm">
                <Megaphone className="w-3.5 h-3.5 text-gold" />
                <span className="text-muted-foreground">Channel:</span>
                <a
                  href={gameConfig.influencerChannelUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-neon text-xs hover:underline truncate max-w-[120px]"
                >
                  {gameConfig.influencerChannelUrl.replace(/^https?:\/\//, "").slice(0, 30)}
                </a>
              </div>
            )}
          </div>

          {/* Dual Viral Loop Diagram */}
          <div className="bg-secondary/30 rounded-lg p-3 space-y-2">
            <p className="text-[10px] uppercase text-muted-foreground font-semibold">Dual Viral Loop</p>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-neon/20 flex items-center justify-center shrink-0">
                  <span className="text-[9px] font-bold text-neon">1</span>
                </div>
                <p className="text-[10px] text-muted-foreground">Influencer sends game to audience</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-neon/20 flex items-center justify-center shrink-0">
                  <span className="text-[9px] font-bold text-neon">2</span>
                </div>
                <p className="text-[10px] text-muted-foreground">Players submit & share for votes</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center shrink-0">
                  <span className="text-[9px] font-bold text-gold">3</span>
                </div>
                <p className="text-[10px] text-muted-foreground">Each share = new voters + new players</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center shrink-0">
                  <span className="text-[9px] font-bold text-gold">4</span>
                </div>
                <p className="text-[10px] text-muted-foreground">Influencer's audience grows exponentially</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Social Distribution Mode */}
      {gameConfig.socialDistributionMode && gameConfig.socialDistributionMode !== "direct" && (
        <div className="glass-panel rounded-xl p-4 space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Social Distribution
          </h3>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Globe className="w-3.5 h-3.5 text-neon" />
              <span className="text-muted-foreground">Mode:</span>
              <Badge variant="outline" className="text-[10px] border-neon/30 text-neon">
                {gameConfig.socialDistributionMode === "hybrid" ? "HYBRID SCORING" : "API CONNECTED"}
              </Badge>
            </div>
            {gameConfig.socialHashtag && (
              <div className="flex items-center gap-2 text-sm">
                <Hash className="w-3.5 h-3.5 text-gold" />
                <span className="text-muted-foreground">Hashtag:</span>
                <span className="font-mono font-bold text-gold">{gameConfig.socialHashtag}</span>
              </div>
            )}
            {gameConfig.socialDistributionMode === "hybrid" && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Social Weight</span>
                  <span className="font-mono font-bold text-gold">{gameConfig.socialEngagementWeight}%</span>
                </div>
                <div className="flex gap-1 h-2 rounded-full overflow-hidden">
                  <div
                    className="bg-neon/60 rounded-l-full transition-all"
                    style={{ width: `${100 - gameConfig.socialEngagementWeight}%` }}
                    title="GP Votes"
                  />
                  <div
                    className="bg-gold/60 rounded-r-full transition-all"
                    style={{ width: `${gameConfig.socialEngagementWeight}%` }}
                    title="Social Engagement"
                  />
                </div>
                <div className="flex justify-between text-[9px] text-muted-foreground">
                  <span className="text-neon">GP Votes {100 - gameConfig.socialEngagementWeight}%</span>
                  <span className="text-gold">Social {gameConfig.socialEngagementWeight}%</span>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Score = Votes + (Likes + Comments×2 + Shares×3) × {gameConfig.socialEngagementWeight}%
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Marketing Materials */}
      {gameConfig.socialCaption && (
        <div className="glass-panel rounded-xl p-4 space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Marketing Materials
          </h3>
          <div className="space-y-2">
            <div className="bg-secondary/50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] uppercase text-muted-foreground">Social Caption</span>
                <CopyButton text={gameConfig.socialCaption} label="Copy" />
              </div>
              <p className="text-xs text-foreground/80 whitespace-pre-line leading-relaxed">
                {gameConfig.socialCaption}
              </p>
            </div>
            {gameConfig.emailPitch && (
              <div className="bg-secondary/50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] uppercase text-muted-foreground">Influencer Pitch</span>
                  <CopyButton text={gameConfig.emailPitch} label="Copy" />
                </div>
                <p className="text-xs text-foreground/80 whitespace-pre-line leading-relaxed line-clamp-6">
                  {gameConfig.emailPitch}
                </p>
              </div>
            )}
            {gameConfig.playerShareTemplate && (
              <div className="bg-secondary/50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] uppercase text-muted-foreground">Player Share Template</span>
                  <CopyButton text={gameConfig.playerShareTemplate} label="Copy" />
                </div>
                <p className="text-xs text-foreground/80 whitespace-pre-line leading-relaxed line-clamp-4">
                  {gameConfig.playerShareTemplate}
                </p>
              </div>
            )}
            {gameConfig.influencerFeatureTemplate && (
              <div className="bg-secondary/50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] uppercase text-muted-foreground">Influencer Feature Post</span>
                  <CopyButton text={gameConfig.influencerFeatureTemplate} label="Copy" />
                </div>
                <p className="text-xs text-foreground/80 whitespace-pre-line leading-relaxed line-clamp-4">
                  {gameConfig.influencerFeatureTemplate}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
